import CryptoJS from 'crypto-js'

const VERSIO_FORMAT = '1.0'

/**
 * Exporta una llista de slots a un Blob .hado.
 * Si s'indica contrasenya, les dades queden encriptades amb AES-256.
 *
 * @param {object[]} slots       - Array de slots de la BD
 * @param {string|null} pwd      - Contrasenya (null = sense encriptació)
 * @returns {Blob}               - Fitxer .hado llest per descarregar
 */
export function exportSlots(slots, pwd = null) {
  const contingut = JSON.stringify(slots)
  const encriptat = pwd != null

  const dades = encriptat
    ? CryptoJS.AES.encrypt(contingut, pwd).toString()
    : contingut

  const sobre = {
    versio:     VERSIO_FORMAT,
    dataExport: new Date().toISOString(),
    encriptat,
    dades,
  }

  return new Blob([JSON.stringify(sobre, null, 2)], { type: 'application/json' })
}

/**
 * Importa slots des d'un fitxer .hado.
 *
 * @param {File} fitxer
 * @param {string|null} pwd  - Contrasenya si el fitxer estava encriptat
 * @returns {Promise<object[]>}
 * @throws {Error} Contrasenya incorrecta, format no vàlid, etc.
 */
export async function importSlots(fitxer, pwd = null) {
  const text = await fitxer.text()
  let sobre

  try {
    sobre = JSON.parse(text)
  } catch {
    throw new Error('Format de fitxer no vàlid: no és JSON')
  }

  if (!sobre.versio || !('dades' in sobre)) {
    throw new Error('El fitxer no és un export .hado vàlid')
  }

  if (sobre.encriptat) {
    if (!pwd) throw new Error('Aquest fitxer requereix contrasenya')
    try {
      const bytes = CryptoJS.AES.decrypt(sobre.dades, pwd)
      const pla   = bytes.toString(CryptoJS.enc.Utf8)
      if (!pla) throw new Error('Contrasenya incorrecta')
      return JSON.parse(pla)
    } catch (e) {
      throw new Error(e.message ?? 'Error desencriptant el fitxer')
    }
  }

  try {
    return JSON.parse(sobre.dades)
  } catch {
    throw new Error('Les dades del fitxer estan malmeses')
  }
}

/**
 * Comprova si un fitxer .hado és encriptat sense importar-lo.
 * Útil per saber si cal demanar contrasenya abans d'intentar la importació.
 *
 * @param {File} fitxer
 * @returns {Promise<boolean>}
 */
export async function isEncrypted(fitxer) {
  try {
    const text = await fitxer.text()
    const sobre = JSON.parse(text)
    return sobre.encriptat === true
  } catch {
    throw new Error('Format de fitxer no vàlid')
  }
}

/**
 * Dispara la descàrrega d'un Blob al navegador.
 */
export function descarregarBlob(blob, nomFitxer) {
  const url = URL.createObjectURL(blob)
  const a   = document.createElement('a')
  a.href     = url
  a.download = nomFitxer
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}
